import React, { useState, useCallback, useRef, useEffect } from "react";
import { Button, Flex, Input, Modal } from "antd";
import { useNavigate } from "react-router-dom";
import {
  CheckCircleSolidIcon,
  GlobeCircleSolidIcon,
  LockCircleSolidIcon,
} from "./../../../assets/icons";
import TextArea from "antd/es/input/TextArea";
import ApiService from "../../../API/apiService";
import { API_ENDPOINTS } from "../../../API/endpoints";

const CHANNEL_TYPES = {
  PUBLIC: "Public",
  PRIVATE: "Private"
};

const MAX_CHANNEL_NAME_LENGTH = 60;

const CreateChannelModal = ({ userSelfData, onModalUpdate, getChannelListApi }) => {
  const navigate = useNavigate();
  const inputRef = useRef();
  
  const [channelName, setChannelName] = useState("");
  const [selectedChannel, setSelectedChannel] = useState(CHANNEL_TYPES.PRIVATE);
  const [channelNameErrMessage, setChannelNameErrMessage] = useState("");
  const [channelNameErr, setChannelNameErr] = useState(false);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const closeModal = useCallback(() => {
    onModalUpdate({ activeModal: "" });
  }, [onModalUpdate]);

  const resetForm = useCallback(() => {
    setChannelName("");
    setSelectedChannel(CHANNEL_TYPES.PRIVATE);
    setChannelNameErrMessage("");
    setChannelNameErr(false);
  }, []);

  const handleCreateChannel = useCallback(async () => {
    const reqBody = {
      name: channelName,
      isPrivate: selectedChannel === CHANNEL_TYPES.PRIVATE,
      description: "",
    };

    try {
      const response = await ApiService.post(API_ENDPOINTS.handlecreatechannel, reqBody);
      
      if (response?.data && response?.status === 1) {
        resetForm();
        navigate(`/elsnertech/channels/${response.data._id}`);
        getChannelListApi();
        closeModal();
      } else if (response?.data?.status === 0) {
        setChannelNameErrMessage(response.data.message);
        setChannelNameErr(true);
      }
    } catch (error) {
      console.error("Error creating channel:", error);
    }
  }, [channelName, selectedChannel, resetForm, navigate, getChannelListApi, closeModal]);

  const handleChannelName = useCallback((e) => {
    const value = e.target.value;
    setChannelNameErrMessage("");
    
    if (value.trim().length <= MAX_CHANNEL_NAME_LENGTH) {
      setChannelName(value);
      setChannelNameErr(false);
    } else {
      setChannelNameErr(true);
      setChannelNameErrMessage(`Channel name should not exceed ${MAX_CHANNEL_NAME_LENGTH} characters`);
    }
  }, []);

  const handleChannelTypeSelection = useCallback((type) => {
    setSelectedChannel(type);
  }, []);

  const isCreateDisabled = channelName.trim().length === 0 || channelNameErr;
  const isAdminUser = userSelfData?.role_name === "Admin";

  const renderChannelTypeButton = useCallback((type, icon, title, description) => {
    const isSelected = selectedChannel === type;
    const isDisabled = type === CHANNEL_TYPES.PUBLIC && !isAdminUser;
    
    return (
      <Button
        type="text"
        className={`private-btn ${isSelected ? "active" : ""}`}
        onClick={() => handleChannelTypeSelection(type)}
        disabled={isDisabled}
      >
        <Flex justify="flex-start" align="center" gap="middle" className="btn-inner-wrapper">
          <span className="btn-icon">{icon}</span>
          <Flex gap="4px" vertical className="btn-text">
            <span className="title">{title}</span>
            <span className="description">{description}</span>
          </Flex>
          {isSelected && (
            <span className="btn-icon">
              <CheckCircleSolidIcon />
            </span>
          )}
        </Flex>
      </Button>
    );
  }, [selectedChannel, isAdminUser, handleChannelTypeSelection]);

  return (
    <Modal
      destroyOnHidden
      title="Create a new channel"
      className="ec-modal create-new-channel-modal"
      open={true}
      onCancel={closeModal}
      footer={[
        <Button key="cancel" onClick={closeModal} className="text-btn">
          Cancel
        </Button>,
        <Button
          key="create"
          type="primary"
          onClick={handleCreateChannel}
          className="primary-btn"
          disabled={isCreateDisabled}
        >
          Create Channel
        </Button>,
      ]}
      width={600}
      style={{ top: 20 }}
    >
      <Flex gap="2px" vertical className="ant-modal-subheader">
        <p className="input-label">Channel Name :</p>
        <Input
          ref={inputRef}
          placeholder="Channel Name"
          value={channelName}
          onChange={handleChannelName}
          maxLength={MAX_CHANNEL_NAME_LENGTH}
        />
        {channelNameErr && (
          <span style={{ color: "red" }}>{channelNameErrMessage}</span>
        )}
      </Flex>
      
      <Flex gap="small" className="public-private-selector">
        {renderChannelTypeButton(
          CHANNEL_TYPES.PUBLIC,
          <GlobeCircleSolidIcon />,
          "Public Channel",
          "Anyone can join"
        )}
        {renderChannelTypeButton(
          CHANNEL_TYPES.PRIVATE,
          <LockCircleSolidIcon />,
          "Private Channel",
          "Only invited people can join"
        )}
      </Flex>
      
      <Flex vertical>
        <TextArea
          placeholder="Enter a purpose for this channel (optional)"
          style={{ height: 120, resize: "none" }}
        />
        <p className="new-channel-modal-purpose-info">
          This will be displayed when browsing for channels.
        </p>
      </Flex>
    </Modal>
  );
};

export default CreateChannelModal;